import React from "react";
import LegalLayout, { LegalSection } from "../components/LegalLayout";
import { brand } from "../mock";

const PrivacyPolicy = () => {
  return (
    <LegalLayout
      eyebrow="Legal"
      title={
        <>
          Privacy <span className="italic text-[#daae9c]">Policy</span>
        </>
      }
      lastUpdated="11 February 2026"
      intro={`${brand.name} respects your privacy. This policy explains what personal information we collect when you enquire about or book our cleaning services, why we collect it, how we use it, and the choices you have. We handle your information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).`}
      seoTitle="Privacy Policy | The Sterling Touch"
      seoDescription="How The Sterling Touch collects, uses and protects the personal information of our Brisbane cleaning clients."
      path="/privacy-policy"
    >
      <LegalSection heading="1. Who we are">
        <p>
          {brand.name} (“we”, “us”, “our”) is a residential cleaning business
          based in {brand.location}. You can reach us at{" "}
          <a
            href={`mailto:${brand.email}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.email}
          </a>{" "}
          or{" "}
          <a
            href={`tel:${brand.phone.replace(/\s/g, "")}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.phone}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="2. Information we collect">
        <p>We only collect information that is reasonably necessary to quote, book and deliver our cleaning service. This typically includes:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Your name, email address, phone number and suburb;</li>
          <li>The property details you share with us (number of bedrooms / bathrooms, type of clean, access notes, special requests);</li>
          <li>Booking history, communication records and feedback;</li>
          <li>Technical information collected automatically when you browse our website, such as IP address, device type, pages viewed and referring URL (via Google Analytics);</li>
          <li>Payment confirmations and invoice details (we do not store full card numbers — payments are processed by our payment provider).</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. How we collect it">
        <p>We collect information directly from you when you:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Submit a form on our website (quote, contact, hero or suburb page);</li>
          <li>Email, call, SMS or message us;</li>
          <li>Book or pay for a clean;</li>
          <li>Leave a review or refer a friend.</li>
        </ul>
        <p>
          We also collect anonymous website analytics through cookies and similar
          technologies. You can disable cookies in your browser at any time — the
          site will still work.
        </p>
      </LegalSection>

      <LegalSection heading="4. Why we use your information">
        <p>We use your personal information to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Respond to enquiries and provide quotes;</li>
          <li>Schedule, deliver and follow up on your clean;</li>
          <li>Process payments and issue receipts/invoices;</li>
          <li>Send you service-related updates (e.g. confirmations, reminders, schedule changes);</li>
          <li>Improve our service, training and website (using aggregated, de-identified data wherever possible);</li>
          <li>Comply with our legal obligations.</li>
        </ul>
        <p>
          We will only send marketing emails or SMS if you have opted in, and you can
          unsubscribe at any time by replying STOP or clicking the unsubscribe link
          in any email.
        </p>
      </LegalSection>

      <LegalSection heading="5. Who we share it with">
        <p>
          We do not sell your personal information. We only share it with trusted
          third parties who help us run our business, and only to the extent
          necessary, including:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Our own cleaning team members assigned to your booking;</li>
          <li>Email delivery providers (Resend);</li>
          <li>Cloud hosting and database providers used to operate this website and our admin tools;</li>
          <li>Payment processors and accounting software;</li>
          <li>Government or law-enforcement agencies where required by law.</li>
        </ul>
        <p>
          Some of these providers may store data outside Australia (for example in
          the United States or the European Union). We take reasonable steps to
          ensure they handle your data securely and consistently with the APPs.
        </p>
      </LegalSection>

      <LegalSection heading="6. How we keep it secure">
        <p>
          We use access controls, encryption in transit (HTTPS), restricted admin
          dashboards and modern authentication to protect your information. No
          internet-based service can be 100% secure — but we take privacy and
          security seriously, and we’ll notify you and the OAIC if an eligible data
          breach ever occurs.
        </p>
      </LegalSection>

      <LegalSection heading="7. How long we keep it">
        <p>
          We keep your information for as long as it’s needed to deliver our
          service, comply with our tax and consumer-law obligations, and resolve
          disputes. Lead enquiries that don’t convert are typically deleted or
          de-identified within 24 months.
        </p>
      </LegalSection>

      <LegalSection heading="8. Your rights">
        <p>You can ask us at any time to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access the personal information we hold about you;</li>
          <li>Correct anything that is inaccurate or out of date;</li>
          <li>Delete your information (subject to our legal obligations);</li>
          <li>Opt out of marketing communications.</li>
        </ul>
        <p>
          Please email{" "}
          <a
            href={`mailto:${brand.email}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.email}
          </a>{" "}
          with your request and we’ll respond within 30 days. If you are not
          satisfied with our response, you can contact the Office of the Australian
          Information Commissioner (OAIC) at{" "}
          <a
            href="https://www.oaic.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#b8866e] underline underline-offset-2"
          >
            oaic.gov.au
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="9. Cookies & analytics">
        <p>
          Our website uses Google Analytics 4 to understand how visitors find and
          use the site. Analytics data is aggregated and does not personally
          identify you. You can opt out of Google Analytics by installing the{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#b8866e] underline underline-offset-2"
          >
            Google Analytics Opt-out Browser Add-on
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="10. Updates to this policy">
        <p>
          We may update this policy from time to time. The most current version
          will always live at{" "}
          <span className="text-ink">/privacy-policy</span> with a clearly marked
          “Last updated” date.
        </p>
      </LegalSection>

      <LegalSection heading="11. Contact us">
        <p>
          Questions about your privacy or this policy? Email{" "}
          <a
            href={`mailto:${brand.email}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.email}
          </a>{" "}
          or call{" "}
          <a
            href={`tel:${brand.phone.replace(/\s/g, "")}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.phone}
          </a>
          . We’d rather sort it out with you directly.
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
